import { ActivityType, AssignmentCategory, AssignmentType, Entity } from "@/enums";
import {
  AmiraResources,
  AssignedSkill,
  Assignment,
  LessonDetails,
  LessonResources,
  LessonSkillGroupMastery,
  parseMasteryPacingToClassName,
  Skill,
  SkillMasteryRecord,
  SkillOriginPriorityOrder,
  SkillStandardResponse,
  Student,
  StudentGroupMastery,
  SubgroupPriorityOrder,
} from "@/models";
import { getDomainFromMapping } from "./getDomainFromMapping";

const findAssignedSkills = (
  mainAssignment: Assignment,
  studentAssignments: Assignment[],
  skills: Skill[]
) => {
  const assignedSkills: {
    [key: string]: AssignedSkill;
  } = {};
  
  /**
   * PROBLEM: When a skill exists in the main assignment but not in student assignments 
   * (e.g., teacher removes activities for a skill from individual students), 
   * those student assignments don't get associated with that skill.
   * This causes incorrect calculations later because the skill appears to have no student data.
   * 
   * SOLUTION: Ensure every skill from the main assignment is associated with every student assignment,
   * even if the student assignment has no activities for that skill (empty manifest for that skill).
   */
  
  // Step 1: Collect all unique skill IDs from the main assignment
  // This gives us the complete list of skills that should be tracked for all students
  const mainSkills = new Set<string>();
  mainAssignment.manifest.forEach((m) => {
    if (m.skills?.length && m.skills[0].skillId) {
      mainSkills.add(m.skills[0].skillId);
    }
  });
  
  // Step 2: Initialize assignedSkills object with all skills from main assignment
  // Each skill starts with just the main assignment associated
  mainAssignment.manifest.forEach((m) => {
    if (m.skills?.length) {
      const skillId = m.skills[0].skillId;
      if (skillId && !assignedSkills[skillId]) {
        const skill = skills.find((s) => s.skillId === skillId);
        if (skill) {
          assignedSkills[skillId] = {
            skill: skill,
            assignments: [mainAssignment],
          };
        }
      }
    }
  });
  
  // Step 3: Associate every student assignment with every skill from main assignment
  // This is the key fix - ensures all students are considered for all skills
  studentAssignments.forEach((studentAssign) => {
    // For each skill that exists in the main assignment...
    mainSkills.forEach((skillId) => {
      const foundAssignedSkill = assignedSkills[skillId];
      if (foundAssignedSkill) {
        // Check if this student assignment is already associated with this skill
        const existingAssignmentIndex = foundAssignedSkill.assignments.findIndex(
          (a) => a.assignmentId === studentAssign.assignmentId
        );
        
        // If not already associated, add the student assignment to this skill
        // This ensures the skill will be calculated for this student, even if they have no activities
        if (existingAssignmentIndex === -1) {
          foundAssignedSkill.assignments.push(studentAssign);
        }
      }
    });
    
    // Step 4: Handle edge case - skills that exist only in student assignments
    // This shouldn't normally happen but provides safety for unexpected data
    studentAssign.manifest.forEach((m) => {
      if (m.skills?.length) {
        const skillId = m.skills[0].skillId;
        if (skillId && !assignedSkills[skillId]) {
          const skill = skills.find((s) => s.skillId === skillId);
          if (skill) {
            // Create new skill entry with both main and student assignments
            assignedSkills[skillId] = {
              skill: skill,
              assignments: [mainAssignment, studentAssign],
            };
          }
        }
      }
    });
  });
  
  /**
   * RESULT: Now every skill from the main assignment is associated with every student assignment.
   * When calculations run later:
   * - Students with individual activities for a skill will use those activities
   * - Students without individual activities will fall back to district/class level assignment
   * - No skills will be missed in the calculations
   */
  
  return assignedSkills;
};

export const calcDisplayAssignmentsCategory = (assignedSkill: AssignedSkill) => {
  // find all possible categories
  const categories = assignedSkill.assignments.reduce((acc, cur) => {
    if (cur.assignmentCategory) {
      cur.assignmentCategory.forEach((cat) => {
        if (!acc.includes(cat)) {
          acc.push(cat);
        }
      });
    }
    return acc;
  }, [] as AssignmentCategory[]);
  // determine which display category by priority
  const origin = calcDisplayAssignmentCategory(categories);

  return origin;
};

export const calcDisplayAssignmentCategory = (categories: AssignmentCategory[]) => {
  return categories.reduce((acc, cur) => {
    const accIndex = SkillOriginPriorityOrder.findIndex((o) => o === acc);
    const curIndex = SkillOriginPriorityOrder.findIndex((o) => o === cur);
    if (accIndex !== -1 && curIndex !== -1 && curIndex < accIndex) {
      return cur;
    }
    return acc;
  }, AssignmentCategory.CORE);
}

const breakdownGroupsByMastery = (
  assignedSkill: AssignedSkill,
  students: Student[],
  masteryRecords: SkillMasteryRecord[],
  studentAssignments: Assignment[]
) => {
  return students.reduce((acc, student) => {
    let domainName = parseMasteryPacingToClassName(null);
    const records = masteryRecords.find(
      (record) => record.studentId === student.id
    );
    if (records && records.records && records.records.length) {
      const skillRecord = records.records.find(
        (r) => r.skillId === assignedSkill.skill.skillId
      );
      if (skillRecord) {
        domainName = parseMasteryPacingToClassName(skillRecord.pacing);
      }
    }

    let assignment = assignedSkill.assignments.find(
      (a) => a.entityType === Entity.STUDENT && a.entityId === student.id
    );
    
    // Only fall back to district if student doesn't have ANY individual assignments (because otherwise if you've removed everything from the skill it'll fall back to district and incorrectly show there is time assigned to the skill)
    if (!assignment) {
      const studentHasAnyIndividualAssignment = studentAssignments.some(a => a.entityId === student.id);
      if (!studentHasAnyIndividualAssignment) {
        assignment = assignedSkill.assignments.find((a) =>
          [Entity.CLASS, Entity.SCHOOL, Entity.DISTRICT].includes(a.entityType)
        );
      }
    }
    


    const foundGroup = acc.find((group) => group.groupName === domainName);
    if (foundGroup) {
      foundGroup.students.push({ student, assignment });
    } else {
      acc.push({
        groupName: domainName,
        timeToGrowth: 0,
        timeAssigned: 0,
        students: [{ student, assignment }],
        resources: 0,
        microlessons: 0,
        tutoring: 0,
        isMicrolessonsAverage: false,
        isResourceAverage: false,
        isTutoringAverage: false,
      });
    }

    return acc;
  }, [] as StudentGroupMastery[]);
};

export const countResourcesAssigned = (lessonResources: LessonResources[], skill: Skill, assignment?: Assignment) => {
  let studentTotalResources = 0;
  // if assignment is undefined, we are looking at a skill group that has no assignment or teacher resources
  if (assignment) {
    if (assignment.entityType === Entity.STUDENT && assignment.resources) {
      // Student has individual assignment - count ONLY their individual teacher resources for this skill
      const studentResourcesForThisSkill = assignment.resources.filter(resourceId => {
        // Check if this resource belongs to the current skill (it may be in multiple skill groups)
        const resourceGroup = lessonResources.find(r => 
          r.skillId === skill.skillId && 
          r.resources.some(resource => resource.resourceUniqueId === resourceId)
        );
        return !!resourceGroup;
      });
      studentTotalResources += studentResourcesForThisSkill.length;
    } else {
      // Student doesn't have individual assignment - use class/district assignment resources
      const foundResources = lessonResources.find(
        (r) => r.skillId === skill.skillId
      );
      if (foundResources) {
        studentTotalResources += foundResources.resources.length;
      }
    }
  }
  
  return studentTotalResources;
}

const aggregateGroupAssignedTypes = (
  studentGroups: StudentGroupMastery[],
  assignedSkill: AssignedSkill,
  lessonResources: LessonResources[],
  timeAssigned: { [key: string]: { [key: string]: { [key: string]: number } } },
  allResources: {skillId: string; resources: AmiraResources[]}[]
) => {
  return studentGroups.map((group) => {
    let {
      timeToGrowth,
      resources,
      microlessons,
      tutoring,
      students,
    } = group;
    const studentIds = students.map(s => s.student.id);



    let totalResources = 0;
    let totalMicrolessons = 0;
    let totalTutoring = 0;
    const skillResources = allResources.find(r => r.skillId === assignedSkill.skill.skillId)?.resources || [];
    const multiStoryResources = skillResources.filter(r => r.storyIds?.length && r.storyIds.length > 0);
   
    const newStudents = students.map((student, index) => {
      let studentTotalMicrolessons = 0;
      let studentTotalTutoring = 0;
      let studentTotalResources = 0;
      if (student.assignment && student.assignment.assignmentType !== AssignmentType.SKIP_OVERRIDE) {
        const processedMultiStoryResources = new Set<string>();
        student.assignment.manifest
          .filter(
            (story) =>
              story.skills &&
              story.skills.length &&
              story.skills[0].skillId === assignedSkill.skill.skillId
          )
          .filter(story => {
            // filter out multi story resources that have already been processed
            const matchedMultiStoryResource = multiStoryResources.find(r => r.storyIds?.includes(story.storyId || ''));
            if (matchedMultiStoryResource && processedMultiStoryResources.has(matchedMultiStoryResource.resourceUniqueId || '')){
              return false;
            }
            if (matchedMultiStoryResource) {
              processedMultiStoryResources.add(matchedMultiStoryResource.resourceUniqueId || '');
            }
            return true;
          }).forEach((story) => {
            if (
              story.activityType === ActivityType.MICRO_LESSON ||
              story.activityType === ActivityType.ISTATION_LEGACY_ACTIVITY
            ) {
              studentTotalMicrolessons += 1;
            } else if (
              story.activityType === ActivityType.AMIRA_STORY ||
              story.activityType === ActivityType.TUTOR
            ) {
              studentTotalTutoring += 1;
            }
          });
      }

      // class or higher entity level assignment on this student use the lesson's default resources for this skill id
      studentTotalResources += countResourcesAssigned(lessonResources, assignedSkill.skill, student.assignment);
      totalMicrolessons += studentTotalMicrolessons;
      totalResources += studentTotalResources;
      totalTutoring += studentTotalTutoring;

      if (index === students.length - 1) {
        if (
          totalResources > 0 &&
          totalResources % students.length !== 0 
        ) {
          group.isResourceAverage = true;
        }
        if (
          totalMicrolessons > 0 &&
          totalMicrolessons % students.length !== 0
        ) {
          group.isMicrolessonsAverage = true;
        }
        if (
          totalTutoring > 0 &&
          totalTutoring % students.length !== 0
        ) {
          group.isTutoringAverage = true;
        }
      }
      if (student.assignment && student.assignment.assignmentType === AssignmentType.SKIP_OVERRIDE) {
        return {...student, assignment: {...student.assignment, manifest: []}};
      }
      return student;
    });

    resources = Math.floor(totalResources / students.length);
    microlessons = Math.floor(totalMicrolessons / students.length);
    tutoring = Math.floor(totalTutoring / students.length);
    let ta = 0;
    const timeEntry = timeAssigned[assignedSkill.skill.skillId || '']?.[group.groupName || ''];
    if (timeEntry) {
      Object.keys(timeEntry).forEach(sId => {
        if (studentIds.includes(sId)) {
          ta += timeEntry[sId];
        }
      });
      if (studentIds.length > 0) {
        ta = Number((ta / studentIds.length).toFixed(0));
      }
    }

    return {
      ...group,
      students: newStudents,
      timeToGrowth,
      resources,
      microlessons,
      tutoring,
      timeAssigned: ta,
    };
  });
};

const calcSkillGroups = (
  assignedSkills: {
    [key: string]: AssignedSkill;
  },
  lessonResources: LessonResources[],
  students: Student[],
  masteryRecords: SkillMasteryRecord[],
  timeAssigned: { [key: string]: { [key: string]: { [key: string]: number } } },
  allResources: {skillId: string; resources: AmiraResources[]}[],
  studentAssignments: Assignment[],
  skillStandards?: SkillStandardResponse[]
) => {
  return Object.keys(assignedSkills).map((key) => {
    const assignedSkill = assignedSkills[key];

    const origin = calcDisplayAssignmentsCategory(assignedSkill);

    // initial breaking down each student into skill groups
    let studentGroups = breakdownGroupsByMastery(
      assignedSkill,
      students,
      masteryRecords || [],
      studentAssignments
    );
    // aggregate total resource, microlesson, tutor assigned and time to growth and time assigned
    studentGroups = aggregateGroupAssignedTypes(
      studentGroups,
      assignedSkill,
      lessonResources,
      timeAssigned,
      allResources
    );

    // Sort student groups according to SubgroupPriorityOrder
    studentGroups.sort((a, b) => {
      const aIndex = SubgroupPriorityOrder.indexOf(a.groupName as any);
      const bIndex = SubgroupPriorityOrder.indexOf(b.groupName as any);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    });

    const standards = [] as string[];
    if (skillStandards && assignedSkill.skill.skillId) {
      const findSkillStandard = skillStandards.find(s => s.skill.skillId === assignedSkill.skill.skillId);
      if (findSkillStandard) {
        findSkillStandard.standards.forEach(element => {
          standards.push(element.standardId);
        });
      }
    }

    const skillGroup = {
      skill: assignedSkill.skill,
      domain: getDomainFromMapping(
        assignedSkill.skill.skillId?.split(".")[0] ?? ""
      ),
      origin, // 1 assignment category viewed by teacher
      standards,
      studentGroups,
    };
    return skillGroup;
  }) as LessonSkillGroupMastery[];
};

export const aggregateSkillGroups = (lesson: LessonDetails, allResources: {skillId: string; resources: AmiraResources[]}[]) => {
  const {
    mainAssignment,
    resources: lessonResources,
    students,
    masteryRecords,
    studentAssignments,
    skills,
    timeAssigned
  } = lesson;

  // determine all assigned skills in this lesson and match them to assignments
  const assignedSkills = findAssignedSkills(
    mainAssignment,
    studentAssignments,
    skills
  );

  return calcSkillGroups(
    assignedSkills,
    lessonResources,
    students,
    masteryRecords,
    timeAssigned,
    allResources,
    studentAssignments,
    lesson.skillStandards   
  );
};
